/**
 * The frame every board pane and dialog shares (spec M1-B4 §2.3, measured).
 *
 * `[ui:library/table-08-sort-pane.json]`: white, `border-radius: 8px`,
 * `box-shadow: rgba(0,0,0,.1) 0 4px 17px 6px`, `padding: 20px 16px 16px`, body text 14 px / 21 px.
 * The group-by dialog uses the same shell with `padding: 0` `[…table-13-group-by.json]`.
 *
 * Every pane closes on `Escape` and returns focus to the control that opened it (A4), and
 * carries the save button that is disabled until the view is dirty — which is how it renders
 * in every capture where nothing was changed.
 *
 * **Measured correction (round 5o).** On the board's **default** table view the button reads
 * **`Save as new view`**, not `Save to this view`: monday has nothing to save *to*, because
 * "Main table" is not a `BoardView` row at all (`Board.views` on the sandbox board lists the
 * form, the broadcast and the dashboard — never the main table). On a real, saved view the
 * same button saves in place. `saveLabel` carries whichever applies, and the pane is told
 * which by the caller rather than deciding for itself.
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useEffect, useRef, type ReactNode } from 'react';

export interface PaneProps {
  title: string;
  /**
   * Rendered **on the title's own line**, immediately after it. monday's quick-filter header
   * is one line — `Quick filters   Showing all of 2967 items  ⓘ` — not a title with the count
   * stacked under it (parity card P-031).
   */
  meta?: ReactNode;
  /** The measured box of this pane, in CSS pixels. */
  width: number;
  height?: number;
  /** Distance from the left edge of the board region, measured on the reference capture. */
  left: number;
  top?: number;
  padded?: boolean;
  dirty?: boolean;
  canSave?: boolean;
  /**
   * `Save to this view` on a saved view, `Save as new view` on the board's default table
   * view — both measured, `discovery/ui-reference/filters/sandbox-quick-filter.png`.
   */
  saveLabel?: string;
  onSave?(): void;
  onClear?(): void;
  clearLabel?: string;
  clearDisabled?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  onClose(): void;
  testId: string;
}

export function Pane({
  title,
  meta,
  width,
  height,
  left,
  top = 8,
  padded = true,
  dirty = false,
  canSave = true,
  saveLabel = 'Save to this view',
  onSave,
  onClear,
  clearLabel = 'Clear all',
  clearDisabled,
  footer,
  children,
  onClose,
  testId,
}: PaneProps) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * A4, and **P-260**. The pane used to carry its own copy of the contract: the opener was read
   * in a passive effect and `.focus()` was called *synchronously* inside the Escape handler,
   * which the browser undoes as it removes the focused node. Both halves now come from the one
   * hook in `packages/ui`, which captures the opener in the commit that mounts the pane and
   * restores it after the paint that removed it. `captureEscape` keeps the capture-phase
   * listener the pane has always used; the hook's stack is what makes a popover opened *inside*
   * the pane take Escape first.
   */
  useOverlayDismiss({ onClose, captureEscape: true });

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div
      ref={ref}
      className="ez-board-pane"
      role="dialog"
      aria-label={title}
      data-testid={testId}
      tabIndex={-1}
      data-padded={padded || undefined}
      style={{ width, height, left, top }}
    >
      <header className="ez-board-pane__header">
        <div className="ez-board-pane__heading">
          <h2 className="ez-board-pane__title">{title}</h2>
          {meta}
        </div>
        <div className="ez-board-pane__actions">
          {onClear ? (
            <button type="button" className="ez-board-pane__link" disabled={clearDisabled} onClick={onClear}>
              {clearLabel}
            </button>
          ) : null}
          {onSave ? (
            <button
              type="button"
              className="ez-board-pane__save"
              data-testid={`${testId}-save`}
              // Disabled until dirty, and refused for a user without `view.update` (F10).
              disabled={!dirty || !canSave}
              onClick={onSave}
            >
              {saveLabel}
            </button>
          ) : null}
        </div>
      </header>
      <div className="ez-board-pane__body">{children}</div>
      {footer ? <footer className="ez-board-pane__footer">{footer}</footer> : null}
    </div>
  );
}

/** The measured geometry of each pane `[B4§2.3]`, relative to the board region's left edge. */
export const PANE_BOX = {
  /**
   * x 640 -> 1440, y 237 -> 680 on a 1440 viewport whose board region starts at x 351 —
   * `filters/sandbox-quick-filter.json` reads `{640, 237, 800, 443}` exactly (P-031).
   */
  quickFilter: { left: 289, width: 800, height: 443 },
  advancedFilter: { left: 289, width: 800, height: 435 },
  /** 556 x 172 at (558, 229). */
  sort: { left: 207, width: 556, height: 172 },
  /** x 875 -> 1215, y 237 -> 590. */
  hide: { left: 524, width: 340, height: 353 },
  /** 533 x 212 at (529, 237). */
  groupBy: { left: 178, width: 533, height: 212 },
  /** x 605 -> 1040, y 237 -> 452. */
  person: { left: 254, width: 435, height: 215 },
  /**
   * Measured (round 5o): board search is **not a pane**. The `Search` toolbar control expands
   * in place into a 266 x 32 input with the placeholder `Search this board` and an `×`, and
   * the other controls slide right. The box is kept here so `Search.tsx` can size itself.
   */
  search: { left: 119, width: 266, height: 32 },
} as const;
